import { JSONObjectEditor } from '@chronosphereio/editors/json';
import { useMemo } from 'react';
import {
  ContextLinkDefinition,
  FormulaDefinition,
  PanelDefinition,
  QueryDefinition,
  QueryOptionsDefinition,
  UnknownSpec,
} from '@/core';
import { ErrorAlert, ErrorBoundary } from '../../../../components';
import { useVizFrameworkConfig } from '../../../model';
import { usePanelPluginByDefinition, usePanelPluginJSONSchema } from '../../../runtime/utils';
import { DataQueryEditor } from '../DataQueryEditor';
import { ContextLinkEditor } from '../LinkEditor';
import { OptionsEditorTabs, OptionsEditorTabsProps } from '../OptionsEditorTabs';

export interface PanelSpecEditorProps {
  panelDefinition: PanelDefinition;
  onQueriesChange: (queries: QueryDefinition[]) => void;
  onQueryOptionsChange: (queryOptions: QueryOptionsDefinition) => void;
  onFormulasChange: (formulas: FormulaDefinition[]) => void;
  onPluginSpecChange: (spec: UnknownSpec) => void;
  onJSONChange: (panelDefinition: PanelDefinition) => void;
  onLinksChange: (links: ContextLinkDefinition[]) => void;
}

export function PanelSpecEditor(props: PanelSpecEditorProps) {
  const {
    panelDefinition,
    onJSONChange,
    onQueriesChange,
    onQueryOptionsChange,
    onPluginSpecChange,
    onLinksChange,
    onFormulasChange,
  } = props;
  const { kind } = panelDefinition.spec.plugin;
  const config = useVizFrameworkConfig();
  const { plugin, dataType, isLoading, error } = usePanelPluginByDefinition(panelDefinition);

  const panelJsonSchema = usePanelPluginJSONSchema(kind);

  const contextLinks = useMemo(() => panelDefinition.spec.links ?? [], [panelDefinition.spec.links]);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  // TODO: Proper loading indicator
  if (isLoading) {
    return null;
  }

  if (plugin === undefined) {
    throw new Error(`Missing implementation for panel plugin with kind '${kind}'`);
  }

  const { panelOptionsEditorComponents, hideQueryEditor, contextLinkConfig, queryEditorCapabilities } = plugin;
  let tabs: OptionsEditorTabsProps['tabs'] = [];

  if (!hideQueryEditor) {
    tabs.push({
      label: 'Query',
      content: (
        <ErrorBoundary FallbackComponent={ErrorAlert}>
          <DataQueryEditor
            formulas={panelDefinition.spec.formulas}
            queries={panelDefinition.spec.queries ?? []}
            onFormulasChange={onFormulasChange}
            onQueriesChange={onQueriesChange}
            showQueryOptions
            queryOptions={panelDefinition.spec.query_options}
            onQueryOptionsChange={onQueryOptionsChange}
            dataType={dataType}
            queryEditorCapabilities={queryEditorCapabilities}
          />
        </ErrorBoundary>
      ),
    });
  }

  if (panelOptionsEditorComponents !== undefined) {
    tabs = tabs.concat(
      panelOptionsEditorComponents.map(({ label, content: OptionsEditorComponent }) => ({
        label,
        content: (
          <ErrorBoundary FallbackComponent={ErrorAlert}>
            <OptionsEditorComponent value={panelDefinition.spec.plugin.spec} onChange={onPluginSpecChange} />
          </ErrorBoundary>
        ),
      }))
    );
  }

  tabs.push({
    label: 'Links',
    content: (
      <ErrorBoundary FallbackComponent={ErrorAlert}>
        <ContextLinkEditor links={contextLinks} onLinksChange={onLinksChange} contextLinkConfig={contextLinkConfig} />
      </ErrorBoundary>
    ),
  });

  // always show json editor by default
  tabs.push({
    label: 'JSON',
    content: (
      <JSONObjectEditor
        value={panelDefinition}
        onChange={onJSONChange}
        jsonSchema={config?.enableSchemaValidation ? panelJsonSchema : undefined}
      />
    ),
  });

  return <OptionsEditorTabs key={tabs.length} tabs={tabs} />;
}
